import { Helmet } from 'react-helmet-async';
import { Shield, Eye, Lock, RefreshCw, FileText } from 'lucide-react';
import { motion } from 'motion/react';

export default function PrivacyPolicy() {
  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Privacy Policy | Open Water Syndicate</title>
        <meta name="description" content="Privacy Policy and security protocols of the Open Water Syndicate global maritime expedition network and logistics platform." />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 text-ows-teal mb-4">
            <Shield className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.4em] uppercase font-mono">Security protocol // SYS-PRIV-300</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-ows-dark leading-none">
            Privacy Policy
          </h1>
          <p className="mt-4 text-xs font-mono text-ows-dark/40 uppercase tracking-widest">
            Last Updated: May 25, 2026 // Active Protocol
          </p>
        </div>

        {/* Overview banner */}
        <div className="mb-12 bg-[#f0faf9] border border-ows-teal/20 p-8 rounded-[2rem] flex flex-col md:flex-row items-start gap-6">
          <div className="p-3 bg-ows-teal/15 rounded-xl text-ows-teal">
            <Lock className="h-6 w-6" />
          </div>
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-[#0d3a40] mb-2">Our Privacy Commitment</h4>
            <p className="text-xs text-ows-dark/80 font-serif leading-relaxed">
              Open Water Syndicate (OWS) runs a rigorous maritime exploration platform. We process sensitive identifiers, 
              safety certificates, physical health clearances, and secure commerce preorders. We never trade, license, or sell 
              your telemetry, credentials, or personal profiles. All information is secured in encrypted cloud datastores.
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-[3rem] border border-ows-teal/5 shadow-xl p-8 sm:p-12 space-y-12">
          {/* Section 1 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 01 // CATEGORIES OF INFORMATION WE LOG</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">1. Information We Collect</h2>
            <div className="space-y-4 text-sm text-ows-dark/70 font-serif leading-relaxed">
              <p>
                To maintain physical safety clearances and fulfill high-performance hardware preorders, we log several categories of data:
              </p>
              <ul className="list-disc pl-5 space-y-2 font-sans text-xs text-ows-dark/80 leading-relaxed">
                <li>
                  <strong className="text-ows-dark">Operator Identifiers:</strong> Legal names, phone numbers, contact emails, shipping/billing addresses, 
                  and secure login credentials (Portal security passkeys and email identifiers).
                </li>
                <li>
                  <strong className="text-ows-dark">Maritime Safety & Physical Clearances:</strong> Signed Digital Liability Waivers, responses to physical standards 
                  disclosures, maritime hazard acceptances, and status logs on external OWS Clearance Certifications (e.g., cert.openwatersyndicate.com).
                </li>
                <li>
                  <strong className="text-ows-dark">Financial & Order Datasets:</strong> Transaction logs, Stripe payment tokens, Batch 001 preorder options, 
                  hardware configuration files, global logistics tracking codes, and shipping quotes. OWS never retains raw credit card details on our local servers.
                </li>
                <li>
                  <strong className="text-ows-dark">Ecosystem Metrics:</strong> Training logs, private coaching schedules, team building itineraries, and historic 
                  expedition attendance records within the Toronto Islands and international channels.
                </li>
              </ul>
            </div>
          </div>

          {/* Section 2 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 02 // METHODOLOGY & PURPOSE OF USE</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">2. Processing Scope</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed mb-4">
              Your data is processed strictly for the execution of OWS mission logistics and ship-building units:
            </p>
            <ul className="list-disc pl-5 space-y-2 font-sans text-xs text-ows-dark/80 leading-relaxed mb-4">
              <li>Deploying custom logistical notifications, calendar invites (ICS attachments), and PDF certificates of achievement.</li>
              <li>Validating digital liability waivers prior to physical launch into Lake Ontario or international waters.</li>
              <li>Calculating customized regional ground transportation shipping rates when your Batch 001 pre-order board arrives at the Toronto Downtown HQ.</li>
              <li>Revising live mission count tracking within the Syndicate Member Portal.</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 03 // SECURE THIRD-PARTY INTEGRATIONS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">3. Data Sharing and Third Parties</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed mb-3">
              We coordinate with a select group of specialized data controllers to facilitate our elite maritime ecosystems:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="p-5 bg-ows-sand/30 rounded-2xl border border-black/5">
                <h4 className="text-[11px] font-bold uppercase tracking-widest text-[#0d3a40] mb-2 font-mono flex items-center gap-1.5 font-bold">
                  <RefreshCw className="h-4 w-4 text-ows-teal animate-spin-slow" /> Stripe Checkout Protocol
                </h4>
                <p className="text-[11px] text-ows-dark/70 leading-relaxed">
                  All commerce transactions, membership fees, and preorder invoices are processed globally via Stripe. Stripe’s strict 
                  encryption standards govern your financial credentials.
                </p>
              </div>
              <div className="p-5 bg-ows-sand/30 rounded-2xl border border-black/5">
                <h4 className="text-[11px] font-bold uppercase tracking-widest text-[#0d3a40] mb-2 font-mono flex items-center gap-1.5 font-bold">
                  <FileText className="h-4 w-4 text-ows-teal" /> Firebase Security Architecture
                </h4>
                <p className="text-[11px] text-ows-dark/70 leading-relaxed">
                  User accounts, credentials, and digital waivers are secured with end-to-end Firebase security rules keeping unauthorized public 
                  browsers blocked from accessing developer structures.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 04 // TIMELINE PROTOCOL RETENTION</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">4. Storage and Data Retention</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              Under our <strong className="text-ows-dark font-sans text-xs uppercase tracking-wider">Open Water Syndicate Timeline Protocol</strong>, preorder records, 
              hardware metrics, and transaction structures are stored securely to compute MOQ batch tracking (representing the system-wide requirement to 
              initiate factory transport pools). We retain digital waiver files indefinitely to defend operational logistics against historical marine claims. 
              Active members can request credential erasure or profile inactivation by securely lodging a transport channel block ticket inside our member support tools.
            </p>
          </div>

          {/* Section 5 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 05 // OUTBOUND SYSTEM SIGNALS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">5. Revisions and Agency</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              OWS reserves the privilege to update this file to accommodate new marine regulations or deep-water research mandates. Updates are published live 
              in the outer borders of our site and active system logs. Continuing to hold active credentials inside our Member Portal constitutes ongoing 
              acceptance of our secure protocols.
            </p>
          </div>
        </div>

        {/* Contact Footer block */}
        <div className="mt-12 text-center">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ows-dark/40">
            Secure inquiries should be dispatched directly to: <span className="text-ows-teal font-bold underline">contact@openwatersyndicate.com</span>
          </p>
        </div>
      </div>
    </div>
  );
}
